import {
  Barcode,
  BarcodeGenerationType,
  BarcodeStatus,
} from '@buncha/driver/types/printScanBarcode';
import {Box, HBox} from '@buncha/components/core/view';
import {CustomText, H6Text} from '@buncha/components/core/text';
import React, {useCallback, useEffect, useMemo} from 'react';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';
import {
  useGetBarcodesForOrders,
  usePrintScanBarcodesContext,
} from '@buncha/driver/hooks/printScanBarcodes';

import BarcodeListSkeleton from '../barcodeListSkeleton';
import CheckIcon from '@buncha/components/core/icon/CheckIcon';
import {CustomFlatList} from '@buncha/components/core/list';
import {HomeScreens} from '@buncha/home/navigation/types';
import If from '@buncha/components/conditional/If';
import {Modules} from '@buncha/utils/types';
import {OrderInfo} from '@buncha/config/graphQL/type';
import PrintBarcodesCTA from '../printBarcodesCTA';
import ReprintBarcodes from '../reprintBarcodes';
import {StyleSheet} from 'react-native';
import {useGetLocale} from '@buncha/localization/hooks';
import {useIsFocused} from '@react-navigation/native';

interface BarcodeListProps {
  orderIds: number[];
  b2BOrderIds: number[];
  hasBagSubmittedByAll: boolean;
  orderInfo: OrderInfo[];
}

const BarcodeList: React.FC<BarcodeListProps> = props => {
  const orderIds = props?.orderIds ?? [];
  const b2BOrderIds = props?.b2BOrderIds ?? [];
  const orderInfo = props?.orderInfo ?? [];
  const theme = useColorThemeType('main');
  const muli = useFontTheme('muli');
  const {state} = usePrintScanBarcodesContext();
  const isFocused = useIsFocused();
  const {getBarcodesForOrders, loading} = useGetBarcodesForOrders();
  const {body} = useGetLocale(Modules.driver, HomeScreens.Driver) ?? {};
  const barcodesList = state.barcodesList;
  const scannedBarcodes = useMemo(() => {
    const scanned = barcodesList.filter(
      (barcode: Barcode) => barcode.status === BarcodeStatus.scanned,
    );
    return scanned.length;
  }, [barcodesList]);

  const getTitleText = useMemo(() => {
    if (state?.barcodeType === BarcodeGenerationType.system)
      return body?.barcodesPrinted;
    return body?.backupSticker;
  }, [body?.backupSticker, body?.barcodesPrinted, state?.barcodeType]);
  const renderItem = useCallback(
    (item: {item: Barcode}) => {
      const iconColor =
        item?.item?.status === BarcodeStatus.generated
          ? theme?.icon?.gray?.[200]
          : theme?.icon?.green?.[100];

      return (
        <HBox style={styles.renderItemBox}>
          <H6Text style={{fontFamily: muli?.[700]?.normal}}>
            {item?.item?.code}
          </H6Text>
          <CheckIcon
            height={20}
            width={20}
            fill={iconColor}
            viewBox="1 -5 40 50"
          />
        </HBox>
      );
    },
    [muli, theme?.icon?.gray, theme?.icon?.green],
  );
  useEffect(() => {
    if (orderInfo.length && isFocused) getBarcodesForOrders({orderInfo});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocused, orderInfo]);

  if (loading) return <BarcodeListSkeleton />;
  return (
    <React.Fragment>
      <If condition={!loading && barcodesList.length}>
        <Box style={styles.parentBox}>
          <CustomText
            style={[
              styles.textAlignment,
              {
                fontFamily: muli?.[600]?.normal,
                color: theme?.text?.gray?.[100],
              },
            ]}>
            {getTitleText}
          </CustomText>

          <H6Text
            style={[
              styles.textAlignment,
              {
                fontFamily: muli?.[700]?.normal,
                color: theme?.text?.blue?.[300],
              },
            ]}>
            {body?.scannedBarcodes}
            {scannedBarcodes}/{barcodesList.length}
          </H6Text>

          <Box style={styles.flatListBox}>
            <CustomFlatList
              contentContainerStyle={styles?.flatList}
              numColumns={2}
              data={barcodesList}
              renderItem={renderItem}
            />
          </Box>
          <If condition={state?.barcodeType === BarcodeGenerationType.system}>
            <ReprintBarcodes
              orderIds={orderIds}
              barcodesLength={barcodesList.length}
              b2BOrderIds={b2BOrderIds}
            />
          </If>
        </Box>
      </If>

      <PrintBarcodesCTA
        barcodesType={state?.barcodeType}
        orderIds={orderIds}
        barcodesGenerated={Boolean(barcodesList.length)}
        numberOfBarcodes={barcodesList.length}
        hasBagSubmittedByAll={props?.hasBagSubmittedByAll}
        b2BOrderIds={b2BOrderIds}
        orderInfo={orderInfo}
      />
    </React.Fragment>
  );
};
export default React.memo(BarcodeList);

const styles = StyleSheet.create({
  parentBox: {
    flex: 0.7,
    gap: 10,
  },
  textAlignment: {
    textAlign: 'left',
  },
  flatList: {
    gap: 15,
    alignSelf: 'flex-start',
  },
  flatListBox: {
    flex: 1,
  },
  renderItemBox: {
    minWidth: '50%',
    justifyContent: 'flex-start',
    gap: 6,
  },
});
